import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { integrationActions } from "../../../redux/integration/integration.slice";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { AtiraToast } from "../../../utils/AtiraToast";
import {
  getFacebookMetaLoginUrl,
  getFacebookMetaRedirectUri,
} from "../utils/integration-utils";

/** Runs the Facebook Login popup and stores what it returns. Shared by the
 * first connect in the modal and the card's reconnect button.
 */
export const useFacebookMetaConnect = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;

  const [connecting, setConnecting] = useState(false);

  const stateRef = useRef<string | null>(null);
  const listenerRef = useRef<((event: MessageEvent) => void) | null>(null);
  const popupTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopListening = useCallback(() => {
    if (listenerRef.current) {
      window.removeEventListener("message", listenerRef.current);
      listenerRef.current = null;
    }

    if (popupTimerRef.current) {
      clearInterval(popupTimerRef.current);
      popupTimerRef.current = null;
    }
  }, []);

  useEffect(() => stopListening, [stopListening]);

  const finishConnecting = async (code: string) => {
    try {
      await dispatch(
        integrationActions.connectFacebookMeta({
          userId,
          code,
          redirectUri: getFacebookMetaRedirectUri(),
        }),
      ).unwrap();
      await dispatch(integrationActions.getUserIntegrations({ userId }));
      AtiraToast.success(t("integration.facebook-meta.modal.connected"));
    } catch (error) {
      AtiraToast.apiError(error);
    } finally {
      setConnecting(false);
    }
  };

  const connect = () => {
    stopListening();

    const state = crypto.randomUUID();
    stateRef.current = state;

    // Opened straight from the click, before anything is awaited, or the
    // browser treats it as an unrequested popup and blocks it
    const popup = window.open(
      getFacebookMetaLoginUrl(state),
      "facebook-meta-connect",
      "width=700,height=800,left=200,top=100",
    );

    if (!popup) {
      AtiraToast.error(t("common.popupBlocked"));
      return;
    }

    setConnecting(true);

    const listener = (event: MessageEvent) => {
      if (
        event.origin !== window.location.origin ||
        event.source !== popup ||
        event.data?.state !== stateRef.current
      ) {
        return;
      }

      stopListening();

      if (event.data?.type === "FACEBOOK_META_CONNECT_SUCCESS") {
        finishConnecting(event.data.code);
      } else {
        setConnecting(false);
      }
    };

    listenerRef.current = listener;
    window.addEventListener("message", listener);

    popupTimerRef.current = setInterval(() => {
      if (popup.closed) {
        stopListening();
        setConnecting(false);
      }
    }, 500);
  };

  return { connect, connecting };
};
